import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, LinearProgress, CircularProgress, Alert as MuiAlert, Chip } from '@mui/material';
import { TrendingUp, Assignment, Business, Assessment, Warning, CheckCircle, ErrorOutline, InfoOutlined } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService, { KPIsResponse, RecentAudit, PeriodTrend, Distribution, Alert, BusinessRanking } from '../../services/analyticsService';
import TrendChart from '../../components/Charts/TrendChart';
import RankingChart from '../../components/Charts/RankingChart';
import DistributionChart from '../../components/Charts/DistributionChart';
import EnhancedStatCard from '../../components/Cards/EnhancedStatCard';
import { formatDate } from '../../utils/dateUtils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<KPIsResponse | null>(null);
  const [recentAudits, setRecentAudits] = useState<RecentAudit[]>([]);
  const [trends, setTrends] = useState<PeriodTrend[]>([]);
  const [distribution, setDistribution] = useState<Distribution[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [rankings, setRankings] = useState<BusinessRanking[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpisData, auditsData, trendsData, distributionData, alertsData] = await Promise.all([
        analyticsService.getKPIs(),
        analyticsService.getRecentAudits(5),
        analyticsService.getTrends({ type: 'general' }),
        analyticsService.getDistribution(),
        analyticsService.getAlerts(),
      ]);
      setKpis(kpisData);
      setRecentAudits(auditsData);
      setTrends(trendsData || []);
      setDistribution(distributionData);
      setAlerts(alertsData);
      if (user?.role === 'admin') {
        const rankingsData = await analyticsService.getRankings({ type: 'businesses', limit: 5, order: 'DESC' });
        setRankings(rankingsData as BusinessRanking[]);
      }
    } catch (err: any) {
      console.error('Error cargando dashboard:', err);
      setError(err.message || 'Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getRoleSpecificStats = () => {
    if (!kpis) return [];
    const adminKpis: any = kpis.kpis;
    const trendData: any = kpis.trends || {};
    
    // Generar datos de sparkline simples basados en tendencias
    const generateSparkline = (baseValue: number) => {
      return Array.from({ length: 7 }, () => baseValue * (0.85 + Math.random() * 0.3));
    };
    
    if (user?.role === 'admin') {
      const avgCompliance = adminKpis.avg_compliance ? parseFloat(adminKpis.avg_compliance) : 0;
      return [
        {
          title: 'Total Negocios',
          value: adminKpis.total_businesses || '0',
          icon: <Business />,
          color: '#1976d2',
          trend: `${adminKpis.total_auditors || '0'} auditores activos`,
          sparklineData: generateSparkline(parseInt(adminKpis.total_businesses || '0')),
        },
        {
          title: 'Auditorías Completadas',
          value: adminKpis.completed_audits || '0',
          icon: <Assessment />,
          color: '#2e7d32',
          trend: `${trendData.audits_month_actual || '0'} este mes`,
          sparklineData: generateSparkline(parseInt(adminKpis.completed_audits || '0') / 7),
        },
        {
          title: 'Promedio General',
          value: avgCompliance ? avgCompliance.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#ed6c02',
          isPercentage: true,
          percentageValue: avgCompliance || 0,
          sparklineData: generateSparkline(avgCompliance || 0),
        },
        {
          title: 'Auditorías Borrador',
          value: adminKpis.draft_audits || '0',
          icon: <Warning />,
          color: '#d32f2f',
          sparklineData: generateSparkline(parseInt(adminKpis.draft_audits || '0')),
        },
      ];
    } else if (user?.role === 'auditor') {
      const miPromedio = adminKpis.my_average ? parseFloat(adminKpis.my_average) : 0;
      return [
        {
          title: 'Mis Auditorías',
          value: adminKpis.my_audits || '0',
          icon: <Assignment />,
          color: '#1976d2',
          trend: `${adminKpis.audits_week || '0'} esta semana`,
          sparklineData: generateSparkline(parseInt(adminKpis.my_audits || '0') / 7),
        },
        {
          title: 'Mi Promedio',
          value: miPromedio ? miPromedio.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#2e7d32',
          isPercentage: true,
          percentageValue: miPromedio || 0,
          sparklineData: generateSparkline(miPromedio || 0),
        },
        {
          title: 'Negocios Asignados',
          value: adminKpis.assigned_businesses || '0',
          icon: <Business />,
          color: '#ed6c02',
          sparklineData: generateSparkline(parseInt(adminKpis.assigned_businesses || '0')),
        },
        {
          title: 'Completadas',
          value: adminKpis.completadas || '0',
          icon: <CheckCircle />,
          color: '#2e7d32',
          sparklineData: generateSparkline(parseInt(adminKpis.completadas || '0')),
        },
      ];
    } else {
      const lastAuditCompliance = adminKpis.ultima_auditoria_cumplimiento ? parseFloat(adminKpis.ultima_auditoria_cumplimiento) : null;
      const promedioTrimestre = adminKpis.promedio_trimestre ? parseFloat(adminKpis.promedio_trimestre) : null;
      const avgCompliance = adminKpis.avg_compliance ? parseFloat(adminKpis.avg_compliance) : null;
      return [
        {
          title: 'Total Auditorías',
          value: adminKpis.total_audits || '0',
          icon: <Assessment />,
          color: '#1976d2',
          sparklineData: generateSparkline(parseInt(adminKpis.total_audits || '0')),
        },
        {
          title: 'Última Auditoría',
          value: lastAuditCompliance ? lastAuditCompliance.toFixed(1) + '%' : '0%',
          icon: <CheckCircle />,
          color: '#2e7d32',
          isPercentage: true,
          percentageValue: lastAuditCompliance || 0,
          sparklineData: generateSparkline(lastAuditCompliance || 0),
        },
        {
          title: 'Promedio General',
          value: avgCompliance ? avgCompliance.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#ed6c02',
          isPercentage: true,
          percentageValue: avgCompliance || 0,
          sparklineData: generateSparkline(avgCompliance || 0),
        },
        {
          title: 'Promedio Trimestre',
          value: promedioTrimestre ? promedioTrimestre.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#1976d2',
          isPercentage: true,
          percentageValue: promedioTrimestre || 0,
          sparklineData: generateSparkline(promedioTrimestre || 0),
        },
      ];
    }
  };

  const getAlertIcon = (type: string) => type === 'error' ? <ErrorOutline /> : type === 'warning' ? <Warning /> : <InfoOutlined />;
  const getAlertSeverity = (type: string): 'error' | 'warning' | 'info' | 'success' => type as 'error' | 'warning' | 'info' | 'success';

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh"><CircularProgress /></Box>;
  if (error) return <Box p={3}><MuiAlert severity="error" onClose={() => setError(null)}>{error}</MuiAlert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>{getWelcomeMessage()}, {user?.name}</Typography>
        <Typography variant="body1" color="textSecondary">Aquí tienes un resumen de tu actividad</Typography>
      </Box>
      <Grid container spacing={3} mb={4}>
        {getRoleSpecificStats().map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <EnhancedStatCard {...stat} />
          </Grid>
        ))}
      </Grid>
      {alerts.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>Alertas y Notificaciones</Typography>
          <Grid container spacing={2}>
            {alerts.map((alert, index) => (
              <Grid item xs={12} key={index}>
                <MuiAlert severity={getAlertSeverity(alert.type)} icon={getAlertIcon(alert.type)}>
                  <Typography variant="body2">{alert.message}</Typography>
                  {alert.details && Array.isArray(alert.details) && alert.details.length > 0 && (
                    <Box mt={1}>
                      {alert.details.map((detail, idx) => (
                        <Chip 
                          key={idx} 
                          label={`${detail.name}: ${parseFloat(detail.average).toFixed(1)}%`} 
                          size="small" 
                          color={parseFloat(detail.average) < 60 ? 'error' : parseFloat(detail.average) < 80 ? 'warning' : 'success'}
                          sx={{ mr: 1, mt: 0.5 }} 
                        />
                      ))}
                    </Box>
                  )}
                </MuiAlert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Grid container spacing={3} mb={4}>
        {trends.length > 0 && (<Grid item xs={12} md={8}><Paper sx={{ p: 3, height: '100%', minHeight: 480 }}><TrendChart data={trends} title="Tendencia de Cumplimiento (Últimos 30 días)" type="area" /></Paper></Grid>)}
        {distribution.length > 0 && (<Grid item xs={12} md={4}><Paper sx={{ p: 3, height: '100%', minHeight: 480 }}><DistributionChart data={distribution} title="Distribución por Estado" /></Paper></Grid>)}
        {user?.role === 'admin' && rankings.length > 0 && (<Grid item xs={12}><Paper sx={{ p: 3 }}><RankingChart data={rankings} title="Top 5 Negocios por Cumplimiento" dataKey="avg_compliance" nameKey="name" /></Paper></Grid>)}
      </Grid>
      {recentAudits.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Auditorías Recientes</Typography>
          <Paper sx={{ p: 2 }}>
            {recentAudits.map((audit) => (
              <Box key={audit.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="bold">{audit.business_name}</Typography>
                  <Typography variant="body2" color="textSecondary">{audit.form_name} • {formatDate(audit.start_date, audit.business_timezone)}</Typography>
                  <Typography variant="caption" color="textSecondary">Auditor: {audit.auditor_name}</Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  {audit.compliance_percentage ? (
                    <>
                      <Typography variant="h6" color={parseFloat(audit.compliance_percentage) >= 80 ? 'success.main' : parseFloat(audit.compliance_percentage) >= 60 ? 'warning.main' : 'error.main'} align="center">{parseFloat(audit.compliance_percentage).toFixed(1)}%</Typography>
                      <LinearProgress variant="determinate" value={parseFloat(audit.compliance_percentage)} color={parseFloat(audit.compliance_percentage) >= 80 ? 'success' : parseFloat(audit.compliance_percentage) >= 60 ? 'warning' : 'error'} sx={{ mt: 1 }} />
                    </>
                  ) : (<Chip label="En progreso" size="small" color="default" />)}
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
